import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Flex, IconButton, Input, Spinner, Text,
} from '@chakra-ui/react';
import {
  LuChevronDown, LuChevronRight, LuEllipsis, LuEyeOff, LuHourglass, LuRotateCcw, LuTrash2, LuTriangleAlert, LuX,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { toAmount } from '../../hooks/useMovementDraft';
import { amountToText, groupForDisplay, parseAmount } from '../../utils/amountParser';
import { formatFigure, formatMoney } from '../../utils/formatters';
import { formatDateShort, formatTime12h } from '../../utils/intlFormatters';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import PhoneConfirmSheet, { DESTRUCTIVE } from './PhoneConfirmSheet';
import PhoneSimilarMovements from './PhoneSimilarMovements';
import {
  AccountIcon, AccountSheet, CategorySheet, CurrencySheet, DateSheet, PickerRow, RowCard, TagSheet, shortDayLabel,
} from './PhonePickers';
import { loadTopCategoryIds } from './quickAddLists';
import { recentAccountIds } from './quickAddStorage';
import IgnoreReasonField from '../movements/IgnoreReasonField';

const TYPES = [
  { value: 'Expenses', labelKey: 'quickAdd.types.expense' },
  { value: 'Income', labelKey: 'quickAdd.types.income' },
  { value: 'Transfer', labelKey: 'quickAdd.types.transfer' },
];

// The optional "why" under an ignore's question, drawn like the quick add's note.
const REASON_FIELD = { mt: '16px', borderRadius: '12px', bg: 'redesign.bg.card', borderColor: 'redesign.border' };

// The line under the sheet, by where the movement came from (`created_by`).
// Anything else, the app's `user` included, was added by hand.
const CREATED_LINE = {
  system: 'phone.sheet.imported',
  chat: 'phone.sheet.createdFromChat',
  mcp: 'phone.sheet.createdFromMcp',
};

const moneyText = (value) => (typeof value === 'number' ? amountToText(value) : (value ?? ''));

/**
 * A foreign amount keeps its two decimals when shown, as its row prints it
 * ("−USD 8.00"). Only the display: the typed text is what the parser reads.
 */
function withCents(shown) {
  if (parseAmount(shown) == null) return shown;
  const dot = shown.lastIndexOf('.');
  if (dot < 0) return `${shown}.00`;
  return shown.length - dot === 2 ? `${shown}0` : shown;
}
// Below this width the pill alone names the currency, so the amount keeps room.
const NARROW = '@media screen and (max-width: 359.98px)';

/** The amount's size: it steps down as the figure grows, so a figure up to 12,345,678.90 stays whole at 320px. */
function amountSize(shown) {
  if (shown.length > 12) return { fontSize: '20px', lineHeight: '26px' };
  if (shown.length > 11) return { fontSize: '22px', lineHeight: '28px' };
  if (shown.length > 8) return { fontSize: '26px', lineHeight: '32px' };
  return { fontSize: '32px', lineHeight: '40px' };
}

/**
 * "USD 300.00", "COP 1,200,000": a foreign currency keeps its decimals, the
 * home currency drops a zero fraction, as the phone rows print them.
 */
function exchangeSide(currency, value, homeCurrency) {
  const n = Number(value) || 0;
  const sign = n < 0 ? '−' : '';
  const figure = currency && currency !== homeCurrency
    ? formatMoney(Math.abs(n), currency, true)
    : formatFigure(n);
  return `${sign}${currency || ''} ${figure}`;
}
const tagIdsOf = (m) => (m?.tag_movements || []).map((tm) => tm.tag.id).sort((a, b) => a - b);
const exchangeKey = (ex) => (ex ? `${ex.currency_target || ''}|${toAmount(moneyText(ex.value_target)) ?? ''}` : '|');

/** "Sat, Sep 26", with the year when it is not this one. */
function dateLabel(iso, t) {
  if (!iso) return '';
  const label = shortDayLabel(iso, t);
  const year = Number(iso.slice(0, 4));
  return year === new Date().getFullYear() ? label : `${label}, ${year}`;
}

/** "Sep 26, 1:12 PM": when the movement was created. */
function createdLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${formatDateShort(iso)}, ${formatTime12h(d)}`;
}

/** "a, b and c" in the user's language. */
function joinList(parts, and) {
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} ${and} ${parts[parts.length - 1]}`;
}

/** The 6px dot beside a value the user changed and has not saved. */
function EditedDot({ show }) {
  const { t } = useTranslation('movements');
  if (!show) return null;
  return (
    <Box
      w="6px"
      h="6px"
      borderRadius="3px"
      bg="phone.link"
      flexShrink={0}
      role="img"
      aria-label={t('phone.sheet.edited')}
    />
  );
}

/**
 * A 52px row of the movement card: the label in an 84px column, the value,
 * and a chevron when a tap opens a picker. With no `onClick` it is text.
 */
function Row({ label, children, onClick, edited, muted }) {
  const interactive = Boolean(onClick);
  return (
    <Box
      as={interactive ? 'button' : 'div'}
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      display="grid"
      gridTemplateColumns={interactive ? '84px minmax(0, 1fr) 20px' : '84px minmax(0, 1fr)'}
      gap="12px"
      alignItems="center"
      w="100%"
      minH="52px"
      px="14px"
      py="4px"
      textAlign="left"
      _active={interactive ? { bg: 'redesign.bg.hover' } : undefined}
    >
      <Text fontSize="15px" lineHeight="20px" color="phone.muted" noOfLines={1}>{label}</Text>
      <Flex align="center" gap="8px" minW={0} color={muted ? 'phone.muted' : 'redesign.heading'} fontSize={interactive ? '16px' : '15px'}>
        <EditedDot show={edited} />
        {children}
      </Flex>
      {interactive && <Box as={LuChevronRight} boxSize="20px" color="phone.muted" />}
    </Box>
  );
}

/** Rows on one card, divided by hairlines inset from the left. */
function Card({ children, mt = '12px' }) {
  return (
    <Box
      mx="16px"
      mt={mt}
      borderRadius="14px"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      overflow="hidden"
      sx={{
        '& > * + *': { position: 'relative' },
        '& > * + *::before': {
          content: '""', position: 'absolute', top: 0, left: '14px', right: 0,
          height: '1px', bg: 'redesign.border',
        },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Expense · Income · Transfer on one 44px track. The columns are equal while
 * the labels fit and give way to the longest one when they do not, so
 * "Transferencia" stays whole at 320px.
 */
function TypeSegments({ options, value, onChange, label, edited }) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      display="grid"
      gridTemplateColumns={`repeat(${options.length}, 1fr)`}
      gap="2px"
      p="2px"
      bg="redesign.bg.sunken"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="12px"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <Box
            key={opt.value}
            as="button"
            type="button"
            role="radio"
            aria-checked={active}
            position="relative"
            h="44px"
            px="8px"
            borderRadius="9px"
            fontSize="15px"
            fontWeight={active ? '600' : '500'}
            whiteSpace="nowrap"
            bg={active ? 'redesign.bg.raised' : 'transparent'}
            color={active ? 'redesign.heading' : 'phone.muted'}
            boxShadow={active ? 'sm' : 'none'}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
            {active && edited && (
              <Box position="absolute" top="6px" right="6px"><EditedDot show /></Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/** A value that truncates on one line. */
function Value({ children }) {
  return <Box as="span" noOfLines={1} minW={0} wordBreak="break-all">{children}</Box>;
}

/**
 * The movement's review reasons, each written out as a sentence. Something to
 * fix is in the alert colour; waiting for the bank is muted. A warning from a
 * statement read twice says so first, then that the movement is fine, and
 * leads to that statement's checkup.
 */
function AlertCard({ reasons, onFix }) {
  const { t } = useTranslation('movements');
  const { close } = usePhoneSheet();
  const leaving = useRef(false);
  if (!reasons.length) return null;
  const allMuted = reasons.every((r) => r.tone === 'muted');
  return (
    <Box
      mx="16px"
      mt="4px"
      px="14px"
      py="12px"
      borderRadius="12px"
      borderLeft="3px solid"
      borderLeftColor={allMuted ? 'phone.muted' : 'phone.alert'}
      bg={allMuted ? 'redesign.bg.card' : 'phone.alertBg'}
      role="status"
    >
      {reasons.map((r) => (
        <Flex key={r.flag} gap="8px" align="flex-start" fontSize="16px" lineHeight="22px" color={r.tone === 'muted' ? 'phone.muted' : 'phone.alertText'} sx={{ '& + &': { mt: '6px' } }}>
          {reasons.length > 1 && (
            <Box as={r.tone === 'muted' ? LuHourglass : LuTriangleAlert} boxSize="16px" flexShrink={0} mt="3px" aria-hidden="true" />
          )}
          <Box flex="1" minW={0}>
            {r.fixFileId != null ? (
              <>
                <Box as="span" display="block" fontWeight="600">{t('detailPanel.alerts.readTwiceTitle')}</Box>
                <Box as="span" display="block">{t('detailPanel.alerts.readTwiceBody')}</Box>
              </>
            ) : (
              <Box as="span">{r.text}</Box>
            )}
            {r.fixFileId != null && onFix && (
              <Button
                variant="phonePrimary"
                mt="10px"
                w="100%"
                h="44px"
                borderRadius="10px"
                fontSize="16px"
                // The sheet goes first: a navigation made under it would be undone.
                onClick={async () => {
                  if (leaving.current) return;
                  leaving.current = true;
                  await close();
                  onFix(r.fixFileId);
                }}
              >
                {t('detailPanel.alerts.fixInStatement')}
              </Button>
            )}
          </Box>
        </Flex>
      ))}
    </Box>
  );
}

/**
 * The phone layout of the movement detail panel: a full-height sheet with the
 * alert card, the amount, the type and 52px rows whose pickers are bottom
 * sheets. It draws the panel's state and runs the panel's handlers, so the
 * alert, direction and save logic stays in `MovementDetailPanel`.
 */
export default function PhoneMovementDetail({
  isOpen,
  onClose,
  loading,
  error,
  movement,
  draft,
  accounts,
  categories,
  currencies,
  tags,
  reasons,
  onFixInStatement,
  converted,
  similar,
  goalsEnabled,
  homeCurrency,
  handleFieldChange,
  setDraft,
  hasChanges,
  onDiscard,
  onSave,
  saving,
  missingOpen,
  onMissingCancel,
  onMissingConfirm,
  onIgnore,
  ignoreReason,
  onIgnoreReasonChange,
  onRestore,
  ignoring,
  restoring,
  onDeleteCheck,
  deleteChecked,
  deleteImpact,
  deleting,
  onDeleteCancel,
  onDeleteConfirm,
}) {
  const { t } = useTranslation(['movements', 'common', 'dates', 'goals']);
  const { can } = useWorkspace();
  const canUpdate = can('movement.update.own');
  const canDelete = can('movement.delete.own');

  const [picker, setPicker] = useState(null);
  const [amountFocused, setAmountFocused] = useState(false);
  const [exchangeFocused, setExchangeFocused] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [deleteStarted, setDeleteStarted] = useState(false);
  // MI-315 — Ignore asks, optionally, why, before it ignores.
  const [ignoreAsking, setIgnoreAsking] = useState(false);

  useEffect(() => {
    if (!isOpen) setPicker(null);
  }, [isOpen]);

  const ready = !loading && !error && movement && draft;
  const ignored = Boolean(movement?.is_ignored);
  // An ignored movement is frozen until it is restored; the backend refuses
  // the write too.
  const editable = Boolean(ready && canUpdate && !ignored);
  const type = draft?.type;
  const isTransfer = type === 'Transfer';

  const account = accounts.find((a) => a.id === draft?.account_id);
  const fromAccount = accounts.find((a) => a.id === draft?.from_account_id);
  const toAccount = accounts.find((a) => a.id === draft?.to_account_id);
  const category = categories.find((c) => c.id === draft?.category_id);

  const changed = useMemo(() => {
    if (!movement || !draft) return new Set();
    const out = new Set();
    if (draft.type !== movement.type) out.add('type');
    ['category_id', 'account_id', 'from_account_id', 'to_account_id', 'date', 'currency'].forEach((key) => {
      if ((draft[key] ?? null) !== (movement[key] ?? null)) out.add(key);
    });
    if ((draft.note || '') !== (movement.note || '')) out.add('note');
    if ((draft.description || '') !== (movement.description || '')) out.add('description');
    if (toAmount(draft.amount) !== toAmount(movement.amount)) out.add('amount');
    if (tagIdsOf(draft).join() !== tagIdsOf(movement).join()) out.add('tags');
    if (exchangeKey(draft.currency_exchange) !== exchangeKey(movement.currency_exchange)) out.add('exchange');
    return out;
  }, [movement, draft]);

  // The alert card follows the saved movement, and clears once a fix is
  // saved. A movement that only lacks its accounts or category says so in
  // words that point at the rows below. An ignored movement raises nothing,
  // as on the server, and its rows are frozen anyway.
  const shownReasons = useMemo(() => {
    if (!movement || movement.is_ignored) return [];
    return (reasons || []).map((r) => {
      if (r.flag !== 'is_incomplete') return r;
      let key;
      if (movement.type === 'Transfer') {
        const noFrom = movement.from_account_id == null;
        const noTo = movement.to_account_id == null;
        key = noFrom && noTo ? 'noFromNoTo' : noFrom ? 'noFrom' : 'noTo';
      } else {
        const noCategory = movement.category_id == null;
        const noAccount = movement.account_id == null;
        key = noCategory && noAccount ? 'noCategoryNoAccount' : noCategory ? 'noCategory' : 'noAccount';
      }
      return { ...r, text: t(`phone.sheet.alerts.${key}`) };
    });
  }, [reasons, movement, t]);

  // The pickers list what is used most first, as quick add's do.
  const [topCategoryIds, setTopCategoryIds] = useState([]);
  useEffect(() => {
    if (picker !== 'category' || type === 'Transfer') return undefined;
    let alive = true;
    loadTopCategoryIds(type).then((ids) => {
      if (alive) setTopCategoryIds(ids.slice(0, 4));
    });
    return () => { alive = false; };
  }, [picker, type]);

  const setType = (next) => {
    if (next === type) return;
    // The account carries over: an expense's account pays the transfer, an
    // income's receives it, and back again.
    const carry = {};
    if (next === 'Transfer' && type === 'Expenses') carry.from_account_id = draft.account_id;
    if (next === 'Transfer' && type === 'Income') carry.to_account_id = draft.account_id;
    if (type === 'Transfer' && next === 'Expenses') carry.account_id = draft.from_account_id;
    if (type === 'Transfer' && next === 'Income') carry.account_id = draft.to_account_id;
    handleFieldChange('type', next);
    Object.entries(carry).forEach(([key, value]) => {
      if (value != null) handleFieldChange(key, value);
    });
  };

  // A refund is stored negative and its sign flips, as the rows print it. It
  // is shown unsigned and kept read-only: the field takes no minus, so an edit
  // would turn it into an ordinary movement.
  const refund = typeof movement?.amount === 'number' && movement.amount < 0;
  const amountText = moneyText(refund && typeof draft?.amount === 'number' ? Math.abs(draft.amount) : draft?.amount);
  const foreign = Boolean(draft?.currency && homeCurrency && draft.currency !== homeCurrency);
  let shownAmount = amountFocused ? amountText : groupForDisplay(amountText);
  if (!amountFocused && foreign && amountText !== '') shownAmount = withCents(shownAmount);
  const amountFont = amountSize(shownAmount);
  const outflow = (type === 'Expenses') !== refund;
  const sign = type === 'Transfer' || !type ? '' : outflow ? '−' : '+';

  // The exchange is editable only while it is missing, which is the fix
  // Review sends here; otherwise it is a read-only fact of the transfer.
  const exchangeEditable = Boolean(editable && isTransfer && movement?.is_exchange_missing);
  const exchange = draft?.currency_exchange;
  const exchangeTarget = exchange?.currency_target
    || (toAccount?.currencies || []).find((c) => c !== draft?.currency)
    || '';
  const setExchange = (patch) => setDraft((prev) => ({
    ...prev,
    currency_exchange: {
      currency_target: prev.currency_exchange?.currency_target || exchangeTarget,
      value_target: prev.currency_exchange?.value_target ?? '',
      ...patch,
    },
  }));

  const tagIds = (draft?.tag_movements || []).map((tm) => tm.tag.id);
  const tagNames = (draft?.tag_movements || []).map((tm) => `#${tm.tag.name}`).join(' ');

  const sourceFiles = [...new Set((movement?.source_files || []).map((sf) => sf.filename).filter(Boolean))];
  const linkedCount = movement?.num_transactions || 0;
  const linkedText = [
    linkedCount ? t('phone.sheet.linkedRows', { count: linkedCount }) : null,
    sourceFiles.length ? `${sourceFiles[0]}${sourceFiles.length > 1 ? ` +${sourceFiles.length - 1}` : ''}` : null,
  ].filter(Boolean).join(' · ');

  const savedExchange = movement?.currency_exchange;
  const showSavedExchange = isTransfer && !exchangeEditable && savedExchange?.currency_target;
  const goal = goalsEnabled ? movement?.goal : null;
  const group = movement?.movement_group;

  // Ignore, Restore and Delete reload or close the sheet, so they wait until
  // the edits are saved or discarded.
  const hasActions = ready && (ignored ? canUpdate : canDelete) && !(editable && hasChanges);

  const requestClose = () => {
    if (hasChanges) setDiscardOpen(true);
    else onClose();
  };

  const closeActions = () => {
    setActionsOpen(false);
    setDeleteStarted(false);
    setIgnoreAsking(false);
    if (deleteChecked) onDeleteCancel();
  };

  const missingTitle = (() => {
    if (!draft) return '';
    if (isTransfer) return t(draft.from_account_id ? 'phone.sheet.missing.noTo' : 'phone.sheet.missing.noFrom');
    if (!draft.category_id && !draft.account_id) return t('phone.sheet.missing.noCategoryNoAccount');
    return t(draft.category_id ? 'phone.sheet.missing.noAccount' : 'phone.sheet.missing.noCategory');
  })();

  const headerButton = (props) => (
    <Flex h="56px" align="center">
      <IconButton
        variant="ghost"
        borderRadius="full"
        color="redesign.text"
        minW="44px"
        minH="44px"
        {...props}
      />
    </Flex>
  );

  // What the server would refuse is named on the button instead, and the tap
  // goes where the fix is.
  const blocked = (() => {
    if (!draft) return null;
    const amount = toAmount(draft.amount);
    if (amount == null || amount === 0) {
      return { label: t('quickAdd.save.enterAmount'), fix: () => document.getElementById('movement-sheet-amount')?.focus() };
    }
    if (!String(draft.note || '').trim()) {
      return { label: t('quickAdd.save.addName'), fix: () => document.getElementById('movement-sheet-what')?.focus() };
    }
    if (isTransfer && !draft.from_account_id && !draft.to_account_id) {
      return { label: t('quickAdd.save.chooseAccount'), fix: () => setPicker('from') };
    }
    return null;
  })();

  const footer = editable && hasChanges ? (
    <Flex gap="10px">
      <Button
        flex="1"
        h="52px"
        borderRadius="14px"
        fontSize="17px"
        fontWeight="600"
        bg="redesign.bg.card"
        border="1px solid"
        borderColor="redesign.border"
        color="redesign.heading"
        onClick={onDiscard}
      >
        {t('detailPanel.buttons.discard')}
      </Button>
      <Button variant="phonePrimary" flex="1.6" onClick={blocked ? blocked.fix : onSave} isLoading={saving}>
        {blocked ? blocked.label : t('detailPanel.buttons.save')}
      </Button>
    </Flex>
  ) : null;

  const deleteOutcome = (() => {
    if (!deleteChecked) return null;
    if (deleteImpact?.will_ignore) {
      return {
        title: t('phone.sheet.delete.ignoreTitle'),
        lines: [t('phone.sheet.delete.ignoreBody'), t('phone.sheet.delete.ignoreKeeps')],
        confirm: t('detailPanel.deleteDialog.ignoreConfirm'),
        destructive: false,
      };
    }
    const parts = [];
    if (deleteImpact?.movement_transactions) parts.push(t('phone.sheet.delete.links', { count: deleteImpact.movement_transactions }));
    if (deleteImpact?.tag_movements) parts.push(t('phone.sheet.delete.tags', { count: deleteImpact.tag_movements }));
    return {
      title: t('phone.sheet.delete.title'),
      lines: [
        t('phone.sheet.delete.plain'),
        parts.length ? t('phone.sheet.delete.alsoRemoves', { list: joinList(parts, t('phone.sheet.delete.and')) }) : null,
        deleteImpact?.movement_groups ? t('phone.sheet.delete.group') : null,
        t('phone.sheet.delete.noFiles'),
      ].filter(Boolean),
      confirm: t('detailPanel.deleteDialog.confirm'),
      destructive: true,
    };
  })();

  return (
    <>
      <PhoneSheet
        id="movement"
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        title={t('phone.sheet.title')}
        headerLeft={headerButton({
          icon: <LuX size={22} />,
          'aria-label': t('common:actions.close'),
          onClick: requestClose,
        })}
        headerRight={hasActions ? headerButton({
          icon: <LuEllipsis size={22} />,
          'aria-label': t('phone.sheet.moreActions'),
          onClick: () => setActionsOpen(true),
        }) : <Box w="44px" />}
        footer={footer}
        isDirty={Boolean(editable && hasChanges)}
        onDirtyClose={() => setDiscardOpen(true)}
      >
        {loading && !error && (
          <Flex justify="center" py="48px"><Spinner size="lg" color="phone.muted" /></Flex>
        )}
        {error && (
          <Text textStyle="phoneBody" color="phone.muted" textAlign="center" px="16px" py="48px">{error}</Text>
        )}
        {ready && (
          <Box pb="24px">
            {ignored && (
              <Flex
                mx="16px"
                mt="4px"
                mb="8px"
                px="14px"
                py="10px"
                gap="12px"
                align="center"
                borderRadius="12px"
                bg="redesign.bg.card"
                borderLeft="3px solid"
                borderLeftColor="phone.muted"
              >
                <Box flex="1" minW={0}>
                  <Text fontSize="15px" fontWeight="600" color="redesign.heading">{t('detailPanel.ignoredBanner.title')}</Text>
                  <Text textStyle="phoneMeta" color="phone.muted">{t('detailPanel.ignoredBanner.description')}</Text>
                  {movement.ignored_reason && (
                    <Text textStyle="phoneMeta" color="redesign.text" mt="4px" whiteSpace="pre-wrap">
                      {t('detailPanel.ignoredBanner.reason', { reason: movement.ignored_reason })}
                    </Text>
                  )}
                </Box>
                {canUpdate && (
                  <Button variant="ghost" color="phone.link" fontSize="16px" fontWeight="600" minH="44px" flexShrink={0} onClick={onRestore} isLoading={restoring}>
                    {t('detailPanel.buttons.restore')}
                  </Button>
                )}
              </Flex>
            )}

            {/* Fixing it in the statement closes the sheet: like Ignore and
                Delete, it waits until the edits are saved or discarded. */}
            <AlertCard reasons={shownReasons} onFix={editable && hasChanges ? null : onFixInStatement} />

            <Flex
              mx="16px"
              mt="14px"
              h="64px"
              align="center"
              justify="space-between"
              gap="4px"
              pl="16px"
              pr="4px"
              borderRadius="14px"
              bg="redesign.bg.card"
              border="1px solid"
              borderColor="redesign.border"
            >
              <Flex
                as="label"
                align="baseline"
                minW={0}
                flex="1"
                color="redesign.heading"
                fontWeight="700"
                letterSpacing="-0.02em"
                textDecoration={ignored ? 'line-through' : undefined}
              >
                <Box as="span" {...amountFont} flexShrink={0}>{sign}</Box>
                <Box
                  as="span"
                  fontSize="20px"
                  fontWeight="600"
                  color="phone.muted"
                  letterSpacing="0"
                  ml="1px"
                  mr="5px"
                  flexShrink={0}
                  sx={{ [NARROW]: { display: 'none' } }}
                >
                  {draft.currency}
                </Box>
                <Input
                  id="movement-sheet-amount"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  data-display-size=""
                  variant="unstyled"
                  {...amountFont}
                  fontWeight="700"
                  h="44px"
                  minW={0}
                  flex="1"
                  color="redesign.heading"
                  letterSpacing="-0.02em"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                  textDecoration={ignored ? 'line-through' : undefined}
                  placeholder="0"
                  _placeholder={{ color: 'redesign.text.faint' }}
                  isReadOnly={!editable || refund}
                  value={shownAmount}
                  onFocus={() => setAmountFocused(true)}
                  onBlur={() => setAmountFocused(false)}
                  onChange={(e) => handleFieldChange('amount', e.target.value.replace(/[^0-9.,]/g, ''))}
                  aria-label={t('quickAdd.amount')}
                />
              </Flex>
              <Flex align="center" gap="6px" flexShrink={0}>
                <EditedDot show={changed.has('amount') || changed.has('currency')} />
                <Box
                  as={editable ? 'button' : 'span'}
                  type={editable ? 'button' : undefined}
                  onClick={editable ? () => setPicker('currency') : undefined}
                  display="flex"
                  alignItems="center"
                  h="44px"
                  px="8px"
                  aria-label={t('quickAdd.currencyAria', { code: draft.currency || '' })}
                >
                  <Flex
                    align="center"
                    gap="4px"
                    h="32px"
                    pl="12px"
                    pr={editable ? '8px' : '12px'}
                    borderRadius="16px"
                    bg="redesign.bg.hover"
                    border="1px solid"
                    borderColor="redesign.border"
                    color="redesign.text"
                    fontSize="14px"
                    fontWeight="600"
                  >
                    {draft.currency}
                    {editable && <Box as={LuChevronDown} boxSize="14px" />}
                  </Flex>
                </Box>
              </Flex>
            </Flex>
            {converted?.isConverted && (
              <Text textStyle="phoneMeta" color="phone.muted" textAlign="right" px="20px" mt="4px">
                {`≈ ${converted.currency} ${formatFigure(converted.amount)}`}
              </Text>
            )}

            <Box
              mx="16px"
              mt="12px"
              opacity={editable ? 1 : 0.6}
              pointerEvents={editable ? undefined : 'none'}
              aria-disabled={editable ? undefined : true}
            >
              <TypeSegments
                label={t('quickAdd.typeAria')}
                value={type}
                onChange={setType}
                edited={changed.has('type')}
                options={TYPES.map((opt) => ({ value: opt.value, label: t(opt.labelKey) }))}
              />
            </Box>

            <Card>
              <Box display="grid" gridTemplateColumns="84px minmax(0, 1fr)" gap="12px" alignItems="center" minH="52px" px="14px">
                <Text as="label" htmlFor="movement-sheet-what" fontSize="15px" color="phone.muted" noOfLines={1}>
                  {t('phone.sheet.fields.name')}
                </Text>
                <Flex align="center" gap="8px" minW={0}>
                  <EditedDot show={changed.has('note')} />
                  <Input
                    // No "name" in the id: it invites iOS contact AutoFill.
                    id="movement-sheet-what"
                    variant="unstyled"
                    h="50px"
                    minW={0}
                    color="redesign.heading"
                    enterKeyHint="done"
                    autoComplete="off"
                    autoCorrect="off"
                    placeholder={t('phone.sheet.namePlaceholder')}
                    _placeholder={{ color: 'phone.muted' }}
                    isReadOnly={!editable}
                    value={draft.note || ''}
                    onChange={(e) => handleFieldChange('note', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </Flex>
              </Box>

              {isTransfer ? (
                <>
                  <Row
                    label={t('detailPanel.fields.from')}
                    onClick={editable ? () => setPicker('from') : undefined}
                    edited={changed.has('from_account_id')}
                    muted={!fromAccount}
                  >
                    {fromAccount && <Box color="phone.muted" flexShrink={0}><AccountIcon account={fromAccount} size={18} /></Box>}
                    <Value>{fromAccount?.name || t('phone.sheet.choose')}</Value>
                  </Row>
                  <Row
                    label={t('detailPanel.fields.to')}
                    onClick={editable ? () => setPicker('to') : undefined}
                    edited={changed.has('to_account_id')}
                    muted={!toAccount}
                  >
                    {toAccount && <Box color="phone.muted" flexShrink={0}><AccountIcon account={toAccount} size={18} /></Box>}
                    <Value>{toAccount?.name || t('phone.sheet.choose')}</Value>
                  </Row>
                  {exchangeEditable && (
                    <Box display="grid" gridTemplateColumns="84px minmax(0, 1fr) auto" gap="12px" alignItems="center" minH="52px" pl="14px" pr="4px">
                      <Text as="label" htmlFor="movement-sheet-exchange" fontSize="15px" color="phone.muted" noOfLines={1}>
                        {t('detailPanel.fields.exchange')}
                      </Text>
                      <Flex align="center" gap="8px" minW={0}>
                        <EditedDot show={changed.has('exchange')} />
                        <Input
                          id="movement-sheet-exchange"
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          variant="unstyled"
                          h="50px"
                          minW={0}
                          fontWeight="600"
                          color="redesign.heading"
                          placeholder="0"
                          _placeholder={{ color: 'phone.muted' }}
                          value={exchangeFocused
                            ? moneyText(exchange?.value_target)
                            : groupForDisplay(moneyText(exchange?.value_target))}
                          onFocus={() => setExchangeFocused(true)}
                          onBlur={() => setExchangeFocused(false)}
                          onChange={(e) => setExchange({ value_target: e.target.value.replace(/[^0-9.,]/g, '') })}
                          aria-label={t('quickAdd.amountReceived', { currency: exchangeTarget })}
                        />
                      </Flex>
                      <Box
                        as="button"
                        type="button"
                        onClick={() => setPicker('exchange-currency')}
                        display="flex"
                        alignItems="center"
                        gap="4px"
                        h="44px"
                        px="10px"
                        color={exchangeTarget ? 'redesign.text' : 'phone.muted'}
                        fontSize="15px"
                        fontWeight="600"
                        aria-label={t('quickAdd.currencyAria', { code: exchangeTarget })}
                      >
                        {exchangeTarget || t('detailPanel.fields.exchangeCurrencyPlaceholder')}
                        <Box as={LuChevronDown} boxSize="14px" color="phone.muted" />
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Row
                    label={t('detailPanel.fields.category')}
                    onClick={editable ? () => setPicker('category') : undefined}
                    edited={changed.has('category_id')}
                    muted={!category}
                  >
                    {category?.icon && <Box as="span" flexShrink={0}>{category.icon}</Box>}
                    <Value>{category?.name || t('phone.sheet.choose')}</Value>
                  </Row>
                  <Row
                    label={t('detailPanel.fields.account')}
                    onClick={editable ? () => setPicker('account') : undefined}
                    edited={changed.has('account_id')}
                    muted={!account}
                  >
                    {account && <Box color="phone.muted" flexShrink={0}><AccountIcon account={account} size={18} /></Box>}
                    <Value>{account?.name || t('phone.sheet.choose')}</Value>
                  </Row>
                </>
              )}

              <Row
                label={t('phone.sheet.fields.date')}
                onClick={editable ? () => setPicker('date') : undefined}
                edited={changed.has('date')}
              >
                <Value>{dateLabel(draft.date, t)}</Value>
              </Row>
              <Row
                label={t('detailPanel.fields.tags')}
                onClick={editable ? () => setPicker('tags') : undefined}
                edited={changed.has('tags')}
                muted={!tagNames}
              >
                <Value>{tagNames || t('detailPanel.fields.tagsPlaceholder')}</Value>
              </Row>
              <Box display="grid" gridTemplateColumns="84px minmax(0, 1fr)" gap="12px" alignItems="center" minH="52px" px="14px">
                <Text as="label" htmlFor="movement-sheet-note" fontSize="15px" color="phone.muted" noOfLines={1}>
                  {t('phone.sheet.fields.note')}
                </Text>
                <Flex align="center" gap="8px" minW={0}>
                  <EditedDot show={changed.has('description')} />
                  <Input
                    id="movement-sheet-note"
                    variant="unstyled"
                    h="50px"
                    minW={0}
                    color="redesign.heading"
                    enterKeyHint="done"
                    autoComplete="off"
                    placeholder={t('detailPanel.description.placeholder')}
                    _placeholder={{ color: 'phone.muted' }}
                    isReadOnly={!editable}
                    value={draft.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </Flex>
              </Box>
            </Card>

            {(goal || showSavedExchange || linkedText || group) && (
              <Card>
                {goal && (
                  <Row label={t('goals:field.label')}>
                    <Value>{`${goal.icon || '🎯'} ${goal.name}`}</Value>
                  </Row>
                )}
                {showSavedExchange && (
                  <Row label={t('detailPanel.fields.exchange')}>
                    <Value>
                      {`${exchangeSide(movement.currency, movement.amount, homeCurrency)} → ${exchangeSide(savedExchange.currency_target, savedExchange.value_target, homeCurrency)}`}
                    </Value>
                  </Row>
                )}
                {linkedText && (
                  <Row label={t('phone.sheet.fields.linked')}>
                    <Value>{linkedText}</Value>
                  </Row>
                )}
                {group && (
                  <Row label={t('phone.sheet.fields.group')}>
                    <Value>{group.name}</Value>
                  </Row>
                )}
              </Card>
            )}

            <PhoneSimilarMovements
              similar={similar?.similar}
              loading={similar?.loading}
              interactive={!(editable && hasChanges)}
            />

            {movement.created_at && (
              <Text textStyle="phoneMeta" color="phone.muted" px="32px" pt="12px">
                {t(CREATED_LINE[movement.created_by] || 'phone.sheet.createdByHand', {
                  when: createdLabel(movement.created_at),
                })}
              </Text>
            )}
            {!canUpdate && (
              <Text textStyle="phoneMeta" color="phone.muted" px="32px" pt="4px">{t('phone.sheet.readOnly')}</Text>
            )}
          </Box>
        )}
      </PhoneSheet>

      {ready && (
        <>
          <AccountSheet
            isOpen={picker === 'account' || picker === 'from' || picker === 'to'}
            onClose={() => setPicker(null)}
            title={picker === 'from' ? t('detailPanel.fields.from') : picker === 'to' ? t('detailPanel.fields.to') : undefined}
            accounts={accounts}
            recentIds={picker ? recentAccountIds() : undefined}
            value={draft[picker === 'from' ? 'from_account_id' : picker === 'to' ? 'to_account_id' : 'account_id']}
            onChange={(acc) => handleFieldChange(
              picker === 'from' ? 'from_account_id' : picker === 'to' ? 'to_account_id' : 'account_id',
              acc?.id ?? null,
            )}
          />
          <CategorySheet
            isOpen={picker === 'category'}
            onClose={() => setPicker(null)}
            categories={categories}
            type={type}
            recentIds={topCategoryIds}
            value={draft.category_id}
            onChange={(c) => handleFieldChange('category_id', c?.id ?? null)}
          />
          <DateSheet
            isOpen={picker === 'date'}
            onClose={() => setPicker(null)}
            value={draft.date}
            onChange={(iso) => handleFieldChange('date', iso)}
          />
          <TagSheet
            isOpen={picker === 'tags'}
            onClose={() => setPicker(null)}
            tags={tags}
            value={tagIds}
            onChange={(ids, chosen) => handleFieldChange(
              'tag_movements',
              chosen.map((tag) => ({ tag_id: tag.id, movement_id: movement.id, tag: { id: tag.id, name: tag.name } })),
            )}
          />
          <CurrencySheet
            isOpen={picker === 'currency' || picker === 'exchange-currency'}
            onClose={() => setPicker(null)}
            currencies={currencies}
            value={picker === 'exchange-currency' ? exchangeTarget : draft.currency}
            onChange={(code) => (picker === 'exchange-currency'
              ? setExchange({ currency_target: code })
              : handleFieldChange('currency', code))}
          />
        </>
      )}

      <PhoneSheet id="movement-actions" isOpen={actionsOpen} onClose={closeActions} size="content">
        <Box px="16px" pt="4px">
          {deleteOutcome ? (
            <>
              <Text textStyle="phoneHeadline" color="redesign.heading" textAlign="center">{deleteOutcome.title}</Text>
              {deleteOutcome.lines.map((line) => (
                <Text key={line} textStyle="phoneMeta" color="phone.muted" textAlign="center" mt="6px">{line}</Text>
              ))}
              {deleteImpact?.will_ignore && (
                <IgnoreReasonField value={ignoreReason} onChange={onIgnoreReasonChange} {...REASON_FIELD} />
              )}
              <Button
                variant="phonePrimary"
                w="100%"
                mt="20px"
                {...(deleteOutcome.destructive ? DESTRUCTIVE : {})}
                isLoading={deleting}
                onClick={onDeleteConfirm}
              >
                {deleteOutcome.confirm}
              </Button>
            </>
          ) : deleteStarted && deleting ? (
            <Flex align="center" justify="center" gap="10px" minH="104px" color="phone.muted">
              <Spinner size="sm" />
              <Text textStyle="phoneBody">{t('phone.sheet.actions.checking')}</Text>
            </Flex>
          ) : ignoreAsking ? (
            <>
              <Text textStyle="phoneHeadline" color="redesign.heading" textAlign="center">{t('detailPanel.ignoreDialog.title')}</Text>
              <Text textStyle="phoneMeta" color="phone.muted" textAlign="center" mt="6px">{t('detailPanel.ignoreDialog.message')}</Text>
              <IgnoreReasonField value={ignoreReason} onChange={onIgnoreReasonChange} {...REASON_FIELD} />
              <Button variant="phonePrimary" w="100%" mt="20px" onClick={() => { closeActions(); onIgnore(); }}>
                {t('detailPanel.ignoreDialog.confirm')}
              </Button>
            </>
          ) : (
            <RowCard mx="0">
              {ignored ? (
                <PickerRow
                  leading={<Box as={LuRotateCcw} boxSize="20px" />}
                  title={t('detailPanel.buttons.restore')}
                  subtitle={t('phone.sheet.actions.restoreHint')}
                  trailing={restoring ? <Spinner size="sm" /> : null}
                  onClick={() => { setActionsOpen(false); onRestore(); }}
                />
              ) : (
                <PickerRow
                  leading={<Box as={LuEyeOff} boxSize="20px" />}
                  title={t('detailPanel.buttons.ignore')}
                  subtitle={t('phone.sheet.actions.ignoreHint')}
                  trailing={ignoring ? <Spinner size="sm" /> : null}
                  onClick={() => { onIgnoreReasonChange(''); setIgnoreAsking(true); }}
                />
              )}
              {!ignored && (
                <PickerRow
                  leading={<Box as={LuTrash2} boxSize="20px" color="phone.expense" />}
                  title={t('detailPanel.buttons.delete')}
                  subtitle={t('phone.sheet.actions.deleteHint')}
                  color="phone.expense"
                  trailing={null}
                  onClick={() => { setDeleteStarted(true); onDeleteCheck(); }}
                />
              )}
            </RowCard>
          )}
          <Button variant="ghost" w="100%" h="52px" mt="8px" fontSize="17px" color="redesign.text" onClick={closeActions}>
            {t('common:actions.cancel')}
          </Button>
        </Box>
      </PhoneSheet>

      <PhoneConfirmSheet
        isOpen={discardOpen}
        title={t('phone.sheet.discardTitle')}
        confirmLabel={t('common:confirm.discard')}
        cancelLabel={t('common:confirm.keepEditing')}
        destructive
        onConfirm={() => { setDiscardOpen(false); onDiscard(); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
      <PhoneConfirmSheet
        isOpen={Boolean(missingOpen)}
        title={missingTitle}
        body={t('phone.sheet.missing.body')}
        confirmLabel={t('quickAdd.maybeSaved.saveAnyway')}
        onConfirm={onMissingConfirm}
        onCancel={onMissingCancel}
      />
    </>
  );
}
